import { Component, inject, input } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import {
  OnyxFormMode,
  OnyxIconButtonComponent,
  OnyxIconComponent,
  OnyxModalService,
  OnyxTooltipDirective,
} from '@onyx/angular';
import type { Subject } from 'rxjs';
import { concatMap, filter, from } from 'rxjs';
import { OrderHelper } from '../../../../../dashboard/orders/common/helpers/order.helper';
import { StorageService } from '../../../../services/storage.service';
import type { DocumentsFormGroup } from '../documents-form.component';
import type {
  DocumentForm,
  DocumentModalData,
  DocumentModalResult,
} from '../documents-modal-form/documents-modal-form.component';
import { DocumentsModalFormComponent } from '../documents-modal-form/documents-modal-form.component';

@Component({
  selector: 'app-documents-form-list',
  imports: [
    OnyxIconComponent,
    OnyxIconButtonComponent,
    OnyxTooltipDirective,
    TranslatePipe,
  ],
  templateUrl: './documents-form-list.component.html',
  styleUrl: './documents-form-list.component.scss',
})
export class DocumentsFormListComponent {
  private readonly modalService = inject(OnyxModalService);
  private readonly storageService = inject(StorageService);
  private readonly orderHelper = inject(OrderHelper);

  public readonly documents = input<DocumentForm[]>();
  public readonly orderDocuments = input<DocumentForm[]>();
  public readonly form = input<DocumentsFormGroup>();
  public readonly uuid = input<string | null>(null);
  public readonly placeholder = input<string>();
  public readonly close$ = input<Subject<void>>();

  protected canEdit(document: DocumentForm): boolean {
    if (!this.orderDocuments()?.length) return false;

    return document.attachments.every((attachment) =>
      this.orderDocuments()?.some(({ attachments }) =>
        attachments.includes(attachment),
      ),
    );
  }

  protected edit(index?: number): void {
    const form = this.form();
    if (index == null || !form) {
      this.orderHelper.edit(this.uuid()!, {
        options: { close$: this.close$() },
      });
      return;
    }

    const document = form.at(index).getRawValue();
    this.modalService
      .open<DocumentModalData, DocumentModalResult>(
        DocumentsModalFormComponent,
        { mode: OnyxFormMode.EDIT, document },
      )
      .result.pipe(filter((document) => document !== undefined))
      .subscribe((document) => {
        if (document === null) return this.remove(index);
        form.at(index).setValue(document);
      });
  }

  protected download(index?: number, orderDocument?: DocumentForm): void {
    const document =
      index != null && this.form()
        ? this.form()?.at(index).getRawValue()
        : orderDocument;
    if (!document) return;

    from(document.attachments)
      .pipe(
        concatMap((attachment) => this.storageService.downloadFile(attachment)),
      )
      .subscribe();
  }

  protected remove(index?: number): void {
    if (index != null) this.form()?.removeAt(index);
  }

  protected showFiles(scans: string[]): void {
    for (const scan of scans) {
      this.storageService.previewFile(scan).subscribe();
    }
  }
}
